import { HttpErrorResponse } from "@angular/common/http";
import { Component, inject, Input, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { TokenPayload } from "@models/finish-sign-up";
import { UserInfo } from "@models/user";
import { catchError, EMPTY, filter, finalize, tap } from "rxjs";
import { AppLayoutComponent } from "../../../shared/components/app-layout/app-layout.component";
import { ModalConfirmationComponent } from "../../../shared/components/modal-confirmation/modal-confirmation.component";
import { PageHeaderComponent } from "../../../shared/components/page-header/page-header.component";
import { AuthService } from "../../services/auth.service";
import { SnackBarService } from "../../services/snack-bar.service";
import { SignupRequestBody } from "../../services/types/signup-request.type";
import { SignupResponseBody } from "../../services/types/signup-response.type";
import { FinishSignUpFormComponent } from "./components/finish-sign-up-form/finish-sign-up-form.component";

@Component({
  selector: "app-finish-sign-up-page",
  imports: [FinishSignUpFormComponent, AppLayoutComponent, PageHeaderComponent],
  templateUrl: "./finish-sign-up-page.component.html",
  styleUrl: "./finish-sign-up-page.component.scss",
})
export class FinishSignUpPageComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly snackbarService = inject(SnackBarService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);

  @Input() params?: string;

  tokenPayload?: TokenPayload;
  currentUserInfo?: UserInfo;

  isLoading = false;

  ngOnInit(): void {
    if (this.params) {
      this.tokenPayload = this.authService.decodeFinishSignUpToken(this.params);
    }
    this.currentUserInfo = this.authService.getLocalStorageUserInfo();
  }

  handleFormSubmit(signupRequest: SignupRequestBody) {
    this.isLoading = true;
    if (this.currentUserInfo) {
      this.updateUser(signupRequest);
    } else {
      this.createUser(signupRequest);
    }
  }

  private createUser(signupRequest: SignupRequestBody) {
    const singupBody: SignupRequestBody = {
      user: {
        ...signupRequest.user,
        invited: false,
      },
    };

    this.authService
      .signup(singupBody)
      .pipe(
        tap((response) => this.handleNewUserSignupSuccess(response)),
        catchError((error: HttpErrorResponse) => {
          this.handleSignupError(error);
          return EMPTY;
        }),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe();
  }

  private updateUser(signupRequest: SignupRequestBody) {
    const updateUserBody: SignupRequestBody = {
      user: {
        ...signupRequest.user,
        roles: this.currentUserInfo?.roles ?? [],
      },
    };

    this.authService
      .updateUserInfo(updateUserBody)
      .pipe(
        tap(() => this.handleUserUpdateSuccess()),
        catchError((error: HttpErrorResponse) => {
          this.handleSignupError(error);
          return EMPTY;
        }),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe();
  }

  private handleNewUserSignupSuccess(response: SignupResponseBody) {
    this.snackbarService.openSnackBar(
      "Cadastro efetuado com sucesso! Confirme seu e-mail para continuar.",
      "Fechar",
      "success",
    );

    const userId = response?.data?.id;
    const userEmail = response?.data?.email;
    if (!userId || !userEmail) {
      this.snackbarService.openSnackBar(
        "Não foi possível iniciar a confirmação da conta.",
        "Fechar",
        "error",
      );
      return;
    }

    this.dialog
      .open(ModalConfirmationComponent, {
        width: "600px",
        disableClose: true,
        data: { userId, userEmail, required: true },
      })
      .afterClosed()
      .pipe(
        filter((confirmed): confirmed is true => confirmed === true),
        tap(() => this.router.navigateByUrl("/my-invitations")),
      )
      .subscribe();
  }

  private handleUserUpdateSuccess() {
    this.snackbarService.openSnackBar(
      "Cadastro atualizado com sucesso!",
      "Fechar",
      "success",
    );
    this.router.navigateByUrl("/my-invitations");
  }

  private handleSignupError(error: HttpErrorResponse) {
    const errorMessage =
      "Não foi possível criar o usuário. O e-mail já esta cadastrado!";
    this.snackbarService.openSnackBar(errorMessage, "Fechar", "error");
    console.error(error);
  }
}
